import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Column, ColumnFactory, RawColumn, Section, SectionFactory } from '../../models';
import { compose } from '../../utils/compose';

interface InOutSource<T extends RawColumn> {
  [key: string]: Array<Section<T>>;
}

export const makeEnhancedColumns = <T extends RawColumn = RawColumn>(columns: T[]) => columns.map(ColumnFactory);

/**
 * It maps through all columns and maps columns with ColumnFactory
 * which enrich the column item with functions or attributes not coming
 * from raw Column values
 */
export const toEnhancedSectionColumn = <T extends RawColumn>({ columns, ...s }: Section<T>): Section<Column> => ({
  ...s,
  columns: makeEnhancedColumns(columns),
});

const composedSectionMapper = compose(toEnhancedSectionColumn, SectionFactory);

/**
 * It transforms InOutSource<T extends Column> and map each columns of each section to EnhancedColumn
 */
export const composeEnhancedSections = <T extends RawColumn>(res: InOutSource<T>) =>
  Object.entries(res).reduce((acc, [k, sections]) => ({ ...acc, [k]: sections.map(composedSectionMapper) }), {});

/**
 * RxJs pipe to process Column response and return a Enhanced Columns response
 * @see EnhancedColumn
 */
export function mapEnhancedSectionColumns<T extends RawColumn = RawColumn, O extends RawColumn = Column>() {
  return (source: Observable<InOutSource<T>>): Observable<InOutSource<O>> => source.pipe(map(composeEnhancedSections));
}
